
import React, { useEffect, useState, useCallback } from 'react';
import { Box, TableBody } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  MainContainer,
  HeaderContainer,
  PageTitle,
  CreateButton,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  ActionButton,
  DeleteIconWrapper
} from './styles';
import CustomPagination from '../../../components/CustomPagination';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { useSidebar } from '../../../context/SidebarContext';
import CancellationReasonModal from './components/CancellationReasonModal';

import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../store';
import {
  searchCancellationReasons,
  deleteCancellationReason
} from '../../../modules/masterdata/actions';
import {
  selectCancellationReasonsList,
  selectSearchCancellationReasonsCount,
  selectDeleteCancellationReasonSuccess,
  selectDeleteCancellationReasonError,
  selectDeleteCancellationReasonMessage,
  selectCreateCancellationReasonSuccess,
  selectCreateCancellationReasonError,
  selectUpdateCancellationReasonSuccess,
  selectUpdateCancellationReasonError,
} from '../../../modules/masterdata/selectors';
import {
  resetDeleteCancellationReasonState,
  resetCreateCancellationReasonState,
  resetUpdateCancellationReasonState
} from '../../../modules/masterdata/reducers';
import AlertPopup from '../../../components/common/AlertPopup';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import { ICancellationReason } from '../../../modules/masterdata/types';

const CancellationReasons: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();

  // Redux State
  const reasonsList = useSelector(selectCancellationReasonsList);
  const totalCount = useSelector(selectSearchCancellationReasonsCount);
  /* Create and Update selectors added */
  const createSuccess = useSelector(selectCreateCancellationReasonSuccess);
  const createError = useSelector(selectCreateCancellationReasonError);
  const updateSuccess = useSelector(selectUpdateCancellationReasonSuccess);
  const updateError = useSelector(selectUpdateCancellationReasonError);

  const deleteSuccess = useSelector(selectDeleteCancellationReasonSuccess);
  const deleteError = useSelector(selectDeleteCancellationReasonError);
  const deleteMessage = useSelector(selectDeleteCancellationReasonMessage);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<ICancellationReason | null>(null);
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);

  // Delete Confirmation State
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteReasonId, setDeleteReasonId] = useState<number | null>(null);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    setActivePage('cancellation-reasons');
    setPath([
      { title: 'Settings', path: '/master-data' },
      { title: 'Master Data', path: '/master-data' },
      { title: 'Cancellation Reasons', path: '/master-data/cancellation-reasons' },
    ]);
    setIsShow(true);

  }, [setActivePage, setPath, setIsShow]);

  const fetchData = useCallback((currentPage: number) => {
    dispatch(searchCancellationReasons({ search: '', page: currentPage, page_size: pageSize }));
  }, [dispatch, pageSize]);

  useEffect(() => {
    fetchData(page);
  }, [fetchData, page]);

  // Handle Delete Success/Error
  // Handle Notifications and Resets - Create/Update/Delete
  useEffect(() => {
    if (createSuccess) {
      setAlert({ message: 'Cancellation reason created successfully', severity: 'success' });
      fetchData(page);
      dispatch(resetCreateCancellationReasonState());
    }
    if (createError) {
      setAlert({ message: createError, severity: 'error' });
      dispatch(resetCreateCancellationReasonState());
    }

    if (updateSuccess) {
      setAlert({ message: 'Cancellation reason updated successfully', severity: 'success' });
      fetchData(page);
      dispatch(resetUpdateCancellationReasonState());
    }
    if (updateError) {
      setAlert({ message: updateError, severity: 'error' });
      dispatch(resetUpdateCancellationReasonState());
    }

    if (deleteSuccess) {
      setAlert({ message: deleteMessage || 'Cancellation reason deleted successfully', severity: 'success' });
      fetchData(page);
      dispatch(resetDeleteCancellationReasonState());
    }
    if (deleteError) {
      setAlert({ message: deleteError, severity: 'error' });
      dispatch(resetDeleteCancellationReasonState());
    }
  }, [
    createSuccess, createError,
    updateSuccess, updateError,
    deleteSuccess, deleteError, deleteMessage,
    dispatch, page, fetchData
  ]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleCreateClick = () => {
    setSelectedReason(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (reason: ICancellationReason) => {
    setSelectedReason(reason);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setDeleteReasonId(id);
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteReasonId !== null) {
      dispatch(deleteCancellationReason(deleteReasonId));
      setIsDeleteConfirmOpen(false);
      setDeleteReasonId(null);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteConfirmOpen(false);
    setDeleteReasonId(null);
  };

  return (
    <Box>
      <MainContainer>
        {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
        <HeaderContainer>
          <PageTitle variant="h6">Manage Cancellation Reasons</PageTitle>
          <CreateButton variant="contained" disableElevation onClick={handleCreateClick}>
            Create Reason
          </CreateButton>
        </HeaderContainer>

        <StyledTableContainer>
          <StyledTable>
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell>Id</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {reasonsList.map((reason, index) => (
                <StyledTableRow key={reason.reason_id}>
                  <StyledTableCell>{reason.reason_id}</StyledTableCell>
                  <StyledTableCell>{reason.reason}</StyledTableCell>
                  <StyledTableCell>{reason.reason_description}</StyledTableCell>
                  <StyledTableCell>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <ActionButton onClick={() => handleEditClick(reason)}>
                        <EditIcon fontSize="small" />
                      </ActionButton>
                      <DeleteIconWrapper onClick={() => handleDeleteClick(reason.reason_id)}>
                        <DeleteIcon fontSize="small" />
                      </DeleteIconWrapper>
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <CustomPagination
            totalPages={Math.ceil(totalCount / pageSize)}
            currentPage={page}
            onPageChange={handlePageChange}
          />
        </Box>
      </MainContainer>

      <CancellationReasonModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          if (isModalOpen) fetchData(page); // Refresh on close if needed, or rely on modal success handling
        }}
        reason={selectedReason}
      />
      <ConfirmationDialog
        open={isDeleteConfirmOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Cancellation Reason"
        message="Are you sure you want to delete this cancellation reason? This action cannot be undone."
      />
    </Box>
  );
};

export default CancellationReasons;
